/**
 * Admin payouts — Pass D step 13.
 *
 * Lists weekly EFT batches (reseller + course). Lets admin:
 *   - Generate new batches on demand (button: "Run reseller batch", "Run course batch")
 *   - Download the CSV for a draft batch (transitions draft → exported)
 *   - Mark a batch sent (after manually wiring it through Scotiabank)
 *   - Mark a batch reconciled (after the EFT clears in our bank statement)
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

export default function PayoutBatches() {
  const [batches, setBatches] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)

  function reload() {
    tournamentApi.get('/admin/payouts/batches')
      .then(r => setBatches(r.data?.batches || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(reload, [])

  async function runBatch(batchType) {
    setBusy(true); setErr(null)
    try {
      await tournamentApi.post('/admin/payouts/batches/run', { batchType })
      reload()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  return (
    <AdminShell title="Payout batches">
      <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, maxWidth: 720 }}>
        Weekly EFT runs for reseller payouts (Mode A net-after-fees) and course payouts
        (when InteGolf settles directly). Batches are generated automatically by the
        weekly cron (Wednesdays 09:00 UTC). Run on demand below if you need an
        off-cycle batch.
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button onClick={() => runBatch('reseller')} disabled={busy} style={primaryBtn}>
          {busy ? 'Running…' : 'Run reseller batch now'}
        </button>
        <button onClick={() => runBatch('course')} disabled={busy} style={secondaryBtn}>
          Run course batch now
        </button>
      </div>

      {err && <div style={{ ...errorBox, marginTop: 16 }}>{err}</div>}

      <h2 style={h2}>Recent batches</h2>
      {!batches ? (
        <div style={{ color: '#64748b' }}>Loading…</div>
      ) : batches.length === 0 ? (
        <div style={emptyBox}>No batches yet. Run one above or wait for the weekly cron.</div>
      ) : (
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={th}>Batch</th>
                <th style={th}>Type</th>
                <th style={th}>Run date</th>
                <th style={th}>Entries</th>
                <th style={th}>Total</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.ID_NO} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={td}>#{b.ID_NO}</td>
                  <td style={td}>{b.BatchType}</td>
                  <td style={td}>{b.RunDate}</td>
                  <td style={td}>{b.EntryCount}</td>
                  <td style={td}>{fmtMoney(b.TotalAmount)}</td>
                  <td style={td}><Pill status={b.Status} /></td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <Link to={`/admin/payouts/${b.ID_NO}`} style={{ color: '#475569', textDecoration: 'none', fontWeight: 600 }}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  )
}

export function PayoutBatchDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)

  function reload() {
    tournamentApi.get('/admin/payouts/batches/' + id)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(reload, [id])

  async function downloadCsv() {
    setBusy(true)
    try {
      const r = await tournamentApi.get(`/admin/payouts/batches/${id}/csv`, { responseType: 'blob' })
      const blob = new Blob([r.data], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payout-${id}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      reload()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function setStatus(status) {
    setBusy(true)
    try {
      await tournamentApi.put(`/admin/payouts/batches/${id}/status`, { status })
      reload()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  if (err) return <AdminShell title="Payout"><div style={errorBox}>{err}</div></AdminShell>
  if (!data) return <AdminShell title="Payout"><div>Loading…</div></AdminShell>

  const { batch, entries } = data
  return (
    <AdminShell title={`Payout batch #${batch.ID_NO}`}>
      <Link to="/admin/payouts" style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>← All batches</Link>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18, marginTop: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <Stat label="Type" value={batch.BatchType} />
          <Stat label="Run date" value={batch.RunDate} />
          <Stat label="Entries" value={batch.EntryCount} />
          <Stat label="Total" value={fmtMoney(batch.TotalAmount)} />
        </div>
        <div style={{ marginTop: 12 }}>Status: <Pill status={batch.Status} /></div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          {batch.Status === 'draft' && (
            <button onClick={downloadCsv} disabled={busy} style={primaryBtn}>
              {busy ? 'Downloading…' : 'Download CSV (mark exported)'}
            </button>
          )}
          {batch.Status === 'exported' && (
            <button onClick={() => setStatus('sent')} disabled={busy} style={primaryBtn}>
              Mark sent (after Scotiabank submission)
            </button>
          )}
          {batch.Status === 'sent' && (
            <button onClick={() => setStatus('reconciled')} disabled={busy} style={primaryBtn}>
              Mark reconciled (after bank statement clears)
            </button>
          )}
        </div>
      </div>

      <h2 style={h2}>Entries</h2>
      <div style={tableWrap}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={th}>Recipient</th>
              <th style={th}>Booking</th>
              <th style={th}>Type</th>
              <th style={th}>Amount</th>
              <th style={th}>Memo</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.ID_NO} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>{e.RecipientName}</td>
                <td style={td}>{e.Booking_ID ? `#${e.Booking_ID}` : '—'}</td>
                <td style={td}>{e.EntryType}</td>
                <td style={td}>{fmtMoney(e.Amount)}</td>
                <td style={{ ...td, color: '#64748b' }}>{e.Memo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  )
}

function Pill({ status }) {
  const palette = {
    draft:      { bg: '#fef3c7', fg: '#92400e' },
    exported:   { bg: '#dbeafe', fg: '#1e40af' },
    sent:       { bg: '#e0e7ff', fg: '#3730a3' },
    reconciled: { bg: '#d1fae5', fg: '#065f46' },
  }[status] || { bg: '#e2e8f0', fg: '#475569' }
  return <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: palette.bg, color: palette.fg, textTransform: 'uppercase', letterSpacing: 0.5 }}>{status}</span>
}
function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  )
}

const th = { textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }
const td = { padding: '12px 14px', verticalAlign: 'top' }
const h2 = { fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginTop: 24, marginBottom: 8 }
const tableWrap = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }
const emptyBox = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 24, color: '#64748b', textAlign: 'center', fontSize: 13 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }

function fmtMoney(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
